import { useState, useLayoutEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import {
  Award,
  Bell,
  BookOpen,
  Briefcase,
  ChevronDown,
  Heart,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  Settings,
  ShoppingBag,
  ShoppingCart,
  User,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CartIcon from "@/components/cart/cart-icon";
import { AuthWrapper } from "@/lib/auth-wrapper";
import { NotificationsDropdown } from "@/components/notifications/NotificationsDropdown";
import { MessagesDropdown } from "@/components/messages/MessagesDropdown";
import { FavoritesDropdown } from "@/components/favorites/FavoritesDropdown";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { useSubscription } from "@/hooks/use-subscription";
import { usePanelAccess } from "@/hooks/use-panel-access";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { hasAnyRole } from "@/lib/normalize-user-data";
import { services } from "@/lib/services";
import { useQuery } from "@tanstack/react-query";

// User session menu component - uses the useAuth hook directly
function UserSessionMenu() {
  // Use the hook directly instead of context for better reliability
  const { user, logout } = useFirebaseAuth();
  const panelAccess = usePanelAccess();
  const subscriptionData = useSubscription();
  const { planName, userSubscriptionLevel } = subscriptionData || {};

  // Fetch professional profile to get profileImageUrl
  const { data: professionalProfile } = useQuery({
    queryKey: ['professionalProfile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      return services.professionalProfile.getUserProfessionalProfile(user.id);
    },
    enabled: !!user?.id,
  });

  // Every panel the user actually has access to — a dual-role (or triple-role)
  // user gets an entry per panel, not just the highest-precedence one. Was
  // previously a single "pick one" link, which silently hid Seller behind
  // Professional (and either behind Instructor) for anyone with more than
  // one role — reported as "I can't see Seller Panel, only Professional."
  const getAccessiblePanels = () => {
    if (!user) return [];

    // Admin UI is now in a separate app; no admin panel link here. An admin
    // gets the panels of the roles they actually hold: an instructor who is
    // also an admin had no Instructor Panel link at all.
    const holds = adminHolds(user);

    const panels: { href: string; label: string; color: string }[] = [];

    if (panelAccess.canAccessInstructorPanel && holds('INSTRUCTOR')) {
      panels.push({ href: '/instructor', label: 'Instructor Panel', color: 'text-purple-400' });
    }

    if (panelAccess.canAccessSellerPanel && holds('SELLER')) {
      panels.push({ href: '/seller', label: 'Seller Panel', color: 'text-green-400' });
    }

    if (panelAccess.canAccessProfessionalPanel && holds('PROFESSIONAL')) {
      panels.push({ href: '/professional', label: 'Professional Panel', color: 'text-emerald-400' });
    }

    return panels;
  };

  const accessiblePanels = getAccessiblePanels();

  const handleLogout = async () => {
    try {
      // Use Firebase logout directly - no API needed
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Get display name - always use firstName + lastName from user profile
  const displayName = user ?
    (user.profile?.firstName && user.profile?.lastName
      ? `${user.profile.firstName} ${user.profile.lastName}`
      : user.profile?.firstName || user.profile?.lastName || user.email?.split('@')[0] || 'User'
    ) : "Guest";

  return (
    <div className="flex items-center space-x-4">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex items-center space-x-1 text-white"
          >
            {/* Use the CachedAvatar component for better avatar handling */}
            <div className="mr-2">
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src={professionalProfile?.profileImageUrl ?? user?.profile?.profileImageUrl ?? undefined}
                  alt={user?.email ?? 'User'}
                />
                <AvatarFallback>
                  {user?.profile?.firstName?.charAt(0) ||
                    user?.profile?.lastName?.charAt(0) ||
                    user?.displayName?.charAt(0) ||
                    user?.email?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </div>
            <span className="max-w-[8rem] truncate 2xl:max-w-none">Hi, {displayName}!</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {user ? (
            <>
              <div className="px-2 py-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <div className="font-medium truncate">{displayName}</div>
                  {(() => {
                    const isAdmin = hasAnyRole(user, ['ADMIN']);
                    if (isAdmin) {
                      return (
                        <div className="text-xs bg-gradient-to-r from-red-500 to-red-600 text-white px-2 py-1 rounded-full font-medium shadow-sm ml-2 flex-shrink-0">
                          Admin
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-center ml-2 flex-shrink-0">
                        <div className={`text-xs px-2 py-1 rounded-full font-medium shadow-sm ${userSubscriptionLevel === 0 ? 'bg-gradient-to-r from-gray-400 to-gray-500 text-white' :
                          userSubscriptionLevel === 10 ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' :
                            userSubscriptionLevel === 20 ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' :
                              userSubscriptionLevel === 30 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black' :
                                'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
                          }`}>
                          {planName}
                        </div>
                        {userSubscriptionLevel === 30 && (
                          <span className="text-xs ml-1">👑</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/dashboard" className="w-full cursor-pointer">
                  My Dashboard
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/my-courses" className="w-full cursor-pointer">
                  My Courses
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/my-certifications" className="w-full cursor-pointer">
                  My Certifications
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/my-purchases" className="w-full cursor-pointer">
                  My Purchases
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/my-jobs" className="w-full cursor-pointer">
                  My Jobs
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/profile" className="w-full cursor-pointer">
                  My Account
                </Link>
              </DropdownMenuItem>

              {/* One entry per panel the user has access to. */}
              {accessiblePanels.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  {accessiblePanels.map((panel) => (
                    <DropdownMenuItem key={panel.href} asChild>
                      <Link href={panel.href} className={`w-full cursor-pointer font-semibold ${panel.color}`}>
                        {panel.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-red-500 cursor-pointer"
                onClick={handleLogout}
              >
                Sign Out
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem asChild>
                <Link href="/auth?tab=login" className="w-full cursor-pointer">
                  Sign In
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/auth?tab=register" className="w-full cursor-pointer">
                  Create Account
                </Link>
              </DropdownMenuItem>
              {/* For debugging purposes */}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-xs text-gray-500">
                Not logged in
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <MessagesDropdown />
      <NotificationsDropdown />
      <FavoritesDropdown />
      <CartIcon />
    </div>
  );
}

// Mobile menu user controls - uses useAuth hook directly
function MobileUserMenu({ onNavigate }: { onNavigate?: () => void }) {
  // Use the hook directly instead of context for better reliability
  const { user, logout } = useFirebaseAuth();
  const panelAccess = usePanelAccess();

  // Fetch professional profile to get profileImageUrl
  const { data: professionalProfile } = useQuery({
    queryKey: ['professionalProfile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      return services.professionalProfile.getUserProfessionalProfile(user.id);
    },
    enabled: !!user?.id,
  });

  // Every panel the user actually has access to — see UserSessionMenu's
  // getAccessiblePanels for why this isn't a single "pick one" link anymore.
  const getAccessiblePanels = () => {
    if (!user) return [];

    const holds = adminHolds(user);

    const panels: { href: string; label: string }[] = [];

    if (panelAccess.canAccessInstructorPanel && holds('INSTRUCTOR')) {
      panels.push({ href: '/instructor', label: 'Instructor Panel' });
    }

    if (panelAccess.canAccessSellerPanel && holds('SELLER')) {
      panels.push({ href: '/seller', label: 'Seller Panel' });
    }

    if (panelAccess.canAccessProfessionalPanel && holds('PROFESSIONAL')) {
      panels.push({ href: '/professional', label: 'Professional Panel' });
    }

    return panels;
  };

  const accessiblePanels = getAccessiblePanels();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const mobileLinkClass =
    "h-11 w-full justify-start rounded-lg border-white/20 text-white hover:bg-white hover:text-black";

  const mobileLinks = [
    { href: "/dashboard", label: "My Dashboard", icon: LayoutDashboard },
    { href: "/profile", label: "My Account", icon: User },
    { href: "/messages", label: "Messages", icon: MessageSquare },
    { href: "/notifications", label: "Notifications", icon: Bell },
    { href: "/favorites", label: "Favorites", icon: Heart },
    { href: "/cart", label: "Cart", icon: ShoppingCart },
    { href: "/my-purchases", label: "My Purchases", icon: ShoppingBag },
    { href: "/my-courses", label: "My Courses", icon: BookOpen },
    { href: "/my-certifications", label: "My Certifications", icon: Award },
    { href: "/my-jobs", label: "My Jobs", icon: Briefcase },
  ];

  return (
    <>
      {/* Use the CachedAvatar component for better avatar handling in mobile menu */}
      <div className="flex justify-center mb-4">
        <div className="w-20 h-20 rounded-full border-2 border-white overflow-hidden">
          <Avatar className="h-20 w-20">
            <AvatarImage
              src={professionalProfile?.profileImageUrl ?? user?.profile?.profileImageUrl ?? undefined}
              alt={user?.email ?? 'User'}
            />
            <AvatarFallback>
              {user?.profile?.firstName?.charAt(0) ||
                user?.profile?.lastName?.charAt(0) ||
                user?.displayName?.charAt(0) ||
                user?.email?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        </div>
      </div>
      <div className="grid gap-2">
        {mobileLinks.map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.href} href={item.href} onClick={onNavigate}>
              <Button variant="outline" className={mobileLinkClass}>
                <Icon className="mr-2 h-4 w-4" />
                {item.label}
              </Button>
            </Link>
          );
        })}
      </div>

      {accessiblePanels.map((panel) => (
        <Link key={panel.href} href={panel.href} onClick={onNavigate}>
          <Button
            variant="outline"
            className="mt-2 h-11 w-full justify-start rounded-lg border-primary text-primary hover:bg-primary hover:text-white"
          >
            <Settings className="mr-2 h-4 w-4" />
            {panel.label}
          </Button>
        </Link>
      ))}
      <Button
        className="mt-2 h-11 w-full justify-start rounded-lg bg-red-600 text-white hover:bg-red-700"
        onClick={() => {
          onNavigate?.();
          void handleLogout();
        }}
      >
        <LogOut className="mr-2 h-4 w-4" />
        Sign Out
      </Button>
    </>
  );
}

// Guest menu component - doesn't require auth context
function GuestMenu() {
  return (
    <div className="flex space-x-2">
      <Link href="/auth?tab=login">
        <Button
          variant="outline"
          className="rounded-full border-white text-white hover:bg-white hover:text-black"
        >
          Sign In
        </Button>
      </Link>
      <Link href="/auth?tab=register">
        <Button className="rounded-full bg-brand text-black hover:bg-brand/90 font-bold">
          Sign Up
        </Button>
      </Link>
    </div>
  );
}

// Mobile guest menu component - doesn't require auth context
function MobileGuestMenu({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <>
      <Link href="/auth?tab=login" onClick={onNavigate}>
        <Button
          variant="outline"
          className="h-11 w-full rounded-lg border-white text-white hover:bg-white hover:text-black"
        >
          Sign In
        </Button>
      </Link>
      <Link href="/auth?tab=register" onClick={onNavigate}>
        <Button className="h-11 w-full rounded-lg bg-brand text-black hover:bg-brand/90 font-bold">
          Get Started
        </Button>
      </Link>
    </>
  );
}

export default function Navbar() {
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // `position: sticky` silently breaks here: the global `html, body, #root {
  // overflow-x: hidden }` rule (added to stop horizontal scroll-bleed) forces
  // overflow-y to compute as 'auto' too — per the CSS Overflow spec, an
  // unpaired 'visible' axis gets flipped to 'auto' the moment the other axis
  // isn't 'visible'. That turns #root/body/html into an overflow context that
  // isn't the one sticky actually anchors to, so the header just scrolls away
  // with the page (reported: "have to scroll to the top again to edit my
  // profile"). `position: fixed` doesn't depend on any ancestor's overflow, so
  // it sidesteps this entirely. Removing it from flow means content needs
  // compensating top padding — measured here (not hardcoded) because the
  // header's real height varies by breakpoint (wraps differently at each of
  // sm/lg), and exposed as a CSS var so every layout that renders <Navbar/>
  // can pick it up without each one re-measuring. A layout effect: measured
  // before the first paint, so the page isn't drawn with the fallback padding
  // and then jumped up (a layout shift on every page load).
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const applyHeight = () => {
      document.documentElement.style.setProperty("--app-navbar-height", `${el.offsetHeight}px`);
    };
    applyHeight();
    const ro = new ResizeObserver(applyHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <header
      ref={headerRef}
      className="fixed inset-x-0 top-0 z-50 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/85"
    >
      <nav className="container mx-auto flex items-center justify-between gap-3 px-4 py-3">
        {/* Logo Section */}
        <Link href="/" className="flex shrink-0 items-center py-2" onClick={() => setMobileMenuOpen(false)}>
          <img
            src="/assets/images/Dance realm logo.png"
            alt="Dance Realm Logo"
            className="w-32 h-auto mr-2 sm:w-40 xl:w-32 2xl:w-40"
          />
        </Link>

        {/* Mobile: cart (with live item-count badge) + menu button. The cart
            was previously only reachable inside the hamburger with no count —
            now it's a visible entry point on the mobile header. */}
        <div className="flex items-center gap-1 xl:hidden">
          <CartIcon />
          <button
            className="flex h-11 w-11 items-center justify-center rounded-lg text-white hover:bg-white/10"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Navigation Links - Desktop */}
        {/* One line at every desktop width: below ~1400px the labels wrapped
            ("Plans / & / Pricing"), squeezed the logo, and the header changed
            height when the signed-in menu replaced the guest one (layout
            shift). The full bar starts at xl; narrower screens get the menu. */}
        <div className="hidden xl:flex flex-grow items-center justify-center">
          <ul className="flex whitespace-nowrap space-x-4 2xl:space-x-6 text-base 2xl:text-lg min-[1800px]:text-xl">
            <li>
              <Link
                href="/"
                className={
                  location === "/"
                    ? "text-brand"
                    : "hover:text-brand transition"
                }
              >
                Home
              </Link>
            </li>
            <li>
              <a
                href="https://conference.dancerealmx.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-brand transition"
              >
                Conference
              </a>
            </li>
            <li>
              <Link
                href="/courses"
                className={
                  location.startsWith("/courses")
                    ? "text-brand"
                    : "hover:text-brand transition"
                }
              >
                Certification
              </Link>
            </li>
            <li>
              <Link
                href="/curriculum"
                className={
                  location.startsWith("/curriculum") ||
                    location.startsWith("/resources")
                    ? "text-brand"
                    : "hover:text-brand transition"
                }
              >
                Curriculum
              </Link>
            </li>
            <li>
              <Link
                href="/connect"
                className={
                  location === "/connect"
                    ? "text-brand"
                    : "hover:text-brand transition"
                }
              >
                Connect & Book
              </Link>
            </li>
            <li>
              <Link
                href="/subscription"
                className={
                  location === "/subscription"
                    ? "text-brand"
                    : "hover:text-brand transition"
                }
              >
                Plans & Pricing
              </Link>
            </li>
          </ul>
        </div>

        {/* Session Container - Desktop */}
        <div className="hidden xl:flex shrink-0 items-center space-x-1 2xl:space-x-4 whitespace-nowrap">
          <ThemeToggle />
          <AuthWrapper fallback={<GuestMenu />}>
            <UserSessionMenu />
          </AuthWrapper>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={`absolute left-0 right-0 top-full max-h-[calc(100dvh-64px)] overflow-y-auto border-t border-white/10 bg-black/95 px-4 pt-3 xl:hidden ${mobileMenuOpen ? "block" : "hidden"}`}
        >
          <ul className="mx-auto flex max-w-screen-sm flex-col space-y-4 pb-4">
            <li>
              <Link
                href="/"
                onClick={() => setMobileMenuOpen(false)}
                className={location === "/" ? "block text-brand" : "block"}
              >
                Home
              </Link>
            </li>
            <li>
              <a
                href="https://conference.dancerealmx.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                onClick={() => setMobileMenuOpen(false)}
              >
                Conference
              </a>
            </li>
            <li>
              <Link
                href="/courses"
                onClick={() => setMobileMenuOpen(false)}
                className={
                  location.startsWith("/courses") ? "block text-brand" : "block"
                }
              >
                Certification
              </Link>
            </li>
            <li>
              <Link
                href="/curriculum"
                onClick={() => setMobileMenuOpen(false)}
                className={
                  location.startsWith("/curriculum") ||
                    location.startsWith("/resources")
                    ? "block text-brand"
                    : "block"
                }
              >
                Curriculum
              </Link>
            </li>
            <li>
              <Link
                href="/connect"
                onClick={() => setMobileMenuOpen(false)}
                className={
                  location === "/connect" ? "block text-brand" : "block"
                }
              >
                Connect & Book
              </Link>
            </li>
            <li>
              <Link
                href="/subscription"
                onClick={() => setMobileMenuOpen(false)}
                className={
                  location === "/subscription"
                    ? "block text-brand"
                    : "block"
                }
              >
                Plans & Pricing
              </Link>
            </li>

            {/* Mobile Session Buttons */}
            <li className="pt-2">
              <div className="flex flex-col space-y-2">
                <AuthWrapper fallback={<MobileGuestMenu onNavigate={() => setMobileMenuOpen(false)} />}>
                  <MobileUserMenu onNavigate={() => setMobileMenuOpen(false)} />
                </AuthWrapper>
              </div>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
}

/**
 * For an admin, whether they hold this role themselves (panel access lets
 * admins into every panel, so it can't tell); everyone else, always.
 */
function adminHolds(user: Parameters<typeof hasAnyRole>[0]) {
  const isAdmin = hasAnyRole(user, ['ADMIN']);
  return (role: string) => !isAdmin || hasAnyRole(user, [role]);
}
